import {
  Box,
  Button,
  Cog6ToothIcon,
  DeleteConfirmButton,
  Drawer,
  DrawerProps,
  Flex,
  InputContainer,
  LocationAutocomplete,
  Menu,
  Text,
  TextField,
  UserSelect,
  toaster,
} from 'components'
import { useAlert, useCurrentUser, useWorkspace } from 'components/providers'
import { useEffect, useRef, useState } from 'react'
import { updateDoc } from 'utils'
import { getFacilityImagePath } from 'utils/facilityUtils'
import { useTemplates } from 'utils/templateUtils'
import { useGoToWorkspace, usePermission } from 'utils/workspaceUtils'

import { SingleUseMediaInput } from 'components/media'
import { isEqual } from 'lodash-es'
import { DBProps, IFacility, PartialBy } from 'types'
import defaultImage from '../../assets/default_tile.png'

export interface Props extends Omit<DrawerProps, 'children' | 'onClose'> {
  /** if id is passed, then update existing document */
  facility?: Facility
  open: boolean
  onClose: (facility?: Facility) => void
  onDelete?: () => Promise<void>
  refetchImage?: () => void
}

type Facility = PartialBy<IFacility, keyof DBProps | 'id'>
type Draft = PartialBy<Facility, 'location' | 'title' | 'template'>

export default function FacilityManage({ facility, open, onClose, onDelete, refetchImage }: Props) {
  const { confirm } = useAlert()
  const user = useCurrentUser()
  const { workspace } = useWorkspace()
  const permission = usePermission()
  const { templates } = useTemplates()
  const [draft, setDraft] = useState<Draft>()
  const [originalState, setOriginalState] = useState<Draft>()
  const [errorFields, setErrorFields] = useState<string[]>([])

  const goToWorkspace = useGoToWorkspace()
  const goBack = () => goToWorkspace(workspace?.id, {})
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open || !user || !workspace || !templates) return

    const inWorkspace = workspace.userIds?.includes(user.id)
    const firstAdminId = Object.keys(workspace.userById).find(
      id => workspace.userById[id].permission === 'admin'
    )
    const firstEditorId = Object.keys(workspace.userById).find(
      id => workspace.userById[id].permission === 'editor'
    )
    const managerId = inWorkspace ? user.id : firstAdminId || firstEditorId || user.id
    const body = facility ?? {
      managerId,
      workspaceId: workspace.id,
      ...(templates.length === 1 && { template: { id: templates[0].id } }),
    }
    setDraft(body)
    setOriginalState(body)
  }, [open, user, workspace, facility, templates])

  if (!draft) return null

  const { id, title = '', template, location, hasImage, managerId } = draft
  const allowManagerEdit = permission.facility === 'admin' || user?.id === managerId
  const isDirty = !isEqual(draft, originalState)

  function addErrorField(field: string) {
    if (!errorFields.includes(field)) setErrorFields([...errorFields, field])
  }

  function removeErrorField(field: string) {
    if (errorFields.includes(field)) setErrorFields(errorFields.filter(f => f !== field))
  }

  async function update(body: Partial<IFacility>) {
    setDraft({ ...draft!, ...body })
  }

  async function handleManagerChange(managerId: string) {
    if (permission.facility !== 'admin') {
      const response = await confirm(
        'You will no longer be able to assign/change the facility manager if you click Confirm. Please reach out to your workspace admin to be added back.',
        { cancelText: 'Cancel', okText: 'Confirm' }
      )
      if (!response) return
    }

    update({ managerId })
  }

  function handleDone() {
    if (!isDirty) return onClose()

    let errors: string[] = []
    if (!title) errors.push('title')
    if (!location) errors.push('location')
    if (!template?.id) errors.push('template')
    if (errors.length) return setErrorFields(errors)

    const body = { ...(draft as Facility), title: title.trim() }
    onClose(body)
  }

  async function handleDelete() {
    if (!onDelete) return

    await onDelete().catch(() =>
      toaster.create({ type: 'error', description: 'Failed to delete facility' })
    )
    goBack()
  }

  async function handleCancel() {
    if (isDirty) {
      const response = await confirm('Are you sure you want to discard changes?', {
        cancelText: 'Cancel',
        okText: 'Discard',
      })
      if (!response) return
    }
    onClose()
  }

  return (
    <Drawer
      onClose={handleCancel}
      open={open}
      header="Manage Facility"
      headerIcon={<Cog6ToothIcon />}
      headerActions={
        permission.facility === 'admin' ? (
          <Menu
            menuProps={{ positioning: { placement: 'bottom-start' } }}
            variant="ghost"
            buttonLabel="Actions"
            mr={6}
            options={[
              {
                type: 'custom',
                label: (
                  <DeleteConfirmButton onDelete={handleDelete} width="100%" borderRadius="none" />
                ),
              },
            ]}
          />
        ) : undefined
      }
      footer={
        <>
          {isDirty && (
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button variant="solid" palette="primary" onClick={handleDone} width="70px">
            {isDirty ? 'Save' : 'Done'}
          </Button>
        </>
      }
    >
      <Flex direction="column" gap={6} ref={contentRef}>
        <TextField
          label="Name"
          placeholder="Please enter facility name"
          value={title}
          onChange={e => {
            update({ title: e.target.value })
            removeErrorField('title')
          }}
          autoFocus
          onBlur={() => !title && addErrorField('title')}
          isError={errorFields.includes('title')}
          helperText={errorFields.includes('title') ? 'Please enter facility name' : ''}
          helperTextProps={{ position: 'absolute' }}
        />

        <LocationAutocomplete
          label="City"
          required
          isError={errorFields.includes('location')}
          helperText={errorFields.includes('location') ? 'Please enter facility location' : ''}
          helperTextProps={{ position: 'absolute' }}
          value={location}
          onChange={location => {
            update({ location })
            if (!location) addErrorField('location')
            else removeErrorField('location')
          }}
          popoverProps={{ portal: { container: contentRef } }}
        />

        {allowManagerEdit && (
          <UserSelect
            label="Facility Manager"
            value={managerId}
            onChange={handleManagerChange}
            permissionsAllowed={['admin', 'editor']}
          />
        )}

        <InputContainer label="Template" required isError={errorFields.includes('template')}>
          <Menu
            variant="outline"
            value={template?.id || ''}
            onChange={id => update({ template: { ...template, id } })}
            buttonLabel={
              templates?.find(t => t.id === template?.id)?.title ||
              (templates?.length === 0 ? 'No templates available' : 'Select a template')
            }
            contentProps={{ width: '408px' }}
            menuProps={{ positioning: { placement: 'top-start' } }}
            options={
              templates?.map(t => ({
                value: t.id,
                label: (
                  <Box>
                    <Text>{t.title}</Text>
                    <Text lineClamp={2} textStyle="bodyXSmall" opacity={0.6}>
                      {t.description.en}
                    </Text>
                  </Box>
                ),
              })) || []
            }
          />
        </InputContainer>

        {id && (
          <SingleUseMediaInput
            type="image"
            defaultImage={defaultImage}
            filePath={getFacilityImagePath(id)}
            noFile={!hasImage}
            onClose={async action => {
              if (action === 'save' && !hasImage) {
                await updateDoc(user!.id, 'facilities', { id, hasImage: true })
              } else if (action === 'delete' && hasImage) {
                await updateDoc(user!.id, 'facilities', { id, hasImage: false })
              }
              refetchImage?.()
            }}
          />
        )}
      </Flex>
    </Drawer>
  )
}
